import { Box, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';
import { useContext } from 'react';
import Ellipsis from 'react-ellipsis-component';
import { useInView } from 'react-intersection-observer';
import xxhash from 'xxhashjs';

import {
  RowOffsetContext,
  useExportMode,
  useSwimlaneRootProps,
} from '@/components/ui/swimlane/context';
import { GRID_GAP, ROW_HEIGHT_MIN } from '../../constants';

interface IHorizontalTitle {
  bottomEntry: IntersectionObserverEntry | undefined;
  children: ReactNode;
  columnHeaderOffset: number;
  menuHeight: number;
}

export function HorizontalTitle({
  bottomEntry,
  children,
  columnHeaderOffset,
  menuHeight,
}: IHorizontalTitle) {
  const { container, height: rowHeight } = useContext(RowOffsetContext);
  const { ref: topRef, entry: topEntry } = useInView({ root: container });
  const { rowHeadersWidth } = useSwimlaneRootProps();
  const { isExporting } = useExportMode();

  // Export clamp: the runtime `headerHeight` below is viewport-relative and
  // goes negative for rows past the small export viewport, so derive the line
  // count from the row's own (viewport-independent) height instead. 0.75rem
  // text at line-height 1.25 = 15px/line; subtract the cell's vertical padding
  // (~28px: 12px top wrapper + 24px pb, rounded). At least one line.
  const EXPORT_LINE_HEIGHT_PX = 15;
  const EXPORT_VERTICAL_PADDING_PX = 28;
  const exportClampLines = Math.max(
    1,
    Math.floor((rowHeight - EXPORT_VERTICAL_PADDING_PX) / EXPORT_LINE_HEIGHT_PX),
  );

  const sl_container_rect = container?.getBoundingClientRect();
  const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
  const scrollOffset = container
    ? Math.max(Math.min(GRID_GAP.value - container.scrollTop, GRID_GAP.value), 0)
    : 0;

  const headerHeight =
    Math.min(
      vh,
      sl_container_rect?.bottom ?? 0,
      bottomEntry?.target.getBoundingClientRect().bottom ?? 0,
    ) -
      Math.max(
        (sl_container_rect?.top ?? 0) + columnHeaderOffset + scrollOffset + menuHeight,
        topEntry?.target.getBoundingClientRect().top ?? 0,
      ) || ROW_HEIGHT_MIN.value;

  // <Ellipsis reflowOnResize={true} /> fails to calculate height properly, so we use a fast hash to create a composite react key
  const key = xxhash
    .h32(`${rowHeadersWidth}-${typeof children === 'string' ? children : ''}`, 0)
    .toString(16);

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <span ref={topRef} style={{ height: '1px', width: '100%' }} />
      </Box>
      <Tooltip title={typeof children === 'string' ? children : ''} followCursor>
        <Box
          component="div"
          data-testid="horizontal"
          style={{
            overflow: 'hidden',
            wordWrap: 'break-word',
            fontSize: '0.75rem',
            lineHeight: '1.25',
            fontWeight: 600,
            letterSpacing: '0.025em',
            textTransform: 'uppercase',
            textAlign: 'center',
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            px: 3,
            pb: 3,
          }}
        >
          {typeof children === 'string' ? (
            // In export mode the layout is non-virtualized and the viewport
            // stays small, so the viewport-relative `headerHeight` math used by
            // react-ellipsis-component goes negative for rows below the fold
            // and collapses the label to a bare "…". Use a CSS line-clamp
            // sized from the row's own height instead: it needs no runtime
            // measurement, and the SVG export walker truncates -webkit-line-
            // clamp with a real "…" for genuinely long labels.
            isExporting ? (
              <span
                className="fs-exclude"
                data-dd-action-name="swimlane label - redacted"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: exportClampLines,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {children}
              </span>
            ) : (
              <Ellipsis
                className="fs-exclude"
                data-dd-action-name="swimlane label - redacted"
                key={key}
                ellipsis={true}
                text={children}
                maxHeight={headerHeight - 16}
                reflowOnResize={false}
              />
            )
          ) : (
            children
          )}
        </Box>
      </Tooltip>
    </>
  );
}
